(function () {
  const parseVariants = (card) => {
    const source = card.querySelector('[data-product-card-variants]');
    if (!source) return [];

    try {
      return JSON.parse(source.textContent) || [];
    } catch (error) {
      console.error('Unable to read product card variants', error);
      return [];
    }
  };

  const initializeCard = (card) => {
    if (!card || card.dataset.uxInitialized === 'true') return;
    card.dataset.uxInitialized = 'true';

    const variants = parseVariants(card);
    const quickOptions = card.querySelector('quick-add-options');
    const swatches = Array.from(card.querySelectorAll('[data-card-color-swatch]'));
    const cardImage = card.querySelector('.product-card__media > .media--first');
    const defaultImageSrc = cardImage?.getAttribute('src') || '';
    const defaultImageSrcset = cardImage?.getAttribute('srcset') || '';
    const colorIndex = quickOptions
      ? Number(quickOptions.dataset.colorIndex)
      : Number(card.querySelector('[data-card-color-swatches]')?.dataset.colorIndex);
    let selectedColor = swatches.find((swatch) => swatch.getAttribute('aria-pressed') === 'true')?.dataset.optionValue;

    if (!selectedColor && Number.isInteger(colorIndex) && colorIndex >= 0) {
      selectedColor = variants.find((variant) => variant.available)?.options?.[colorIndex] || '';
    }

    if (quickOptions && typeof quickOptions.setProductData === 'function') {
      quickOptions.setProductData(variants, selectedColor);
    }

    swatches.forEach((swatch) => {
      swatch.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();

        selectedColor = swatch.dataset.optionValue || '';
        swatches.forEach((item) => {
          const isActive = item === swatch;
          item.classList.toggle('is-active', isActive);
          item.setAttribute('aria-pressed', String(isActive));
        });

        if (quickOptions && typeof quickOptions.setSelectedColor === 'function') {
          quickOptions.setSelectedColor(selectedColor);
        }

        if (swatch.dataset.changeImage === 'true' && cardImage) {
          if (swatch.dataset.imageSrc) {
            cardImage.src = swatch.dataset.imageSrc;
            cardImage.removeAttribute('srcset');
          } else if (defaultImageSrc) {
            cardImage.src = defaultImageSrc;
            if (defaultImageSrcset) cardImage.setAttribute('srcset', defaultImageSrcset);
          }
        }

        const variant = variants.find(
          (item) => item.available && item.options?.[colorIndex] === selectedColor
        );
        const productLinks = card.querySelectorAll('.product-card__link-overlay, .product-card__title a');
        if (variant) {
          productLinks.forEach((productLink) => {
            const baseUrl = productLink.getAttribute('href').split('?')[0];
            productLink.setAttribute('href', `${baseUrl}?variant=${variant.id}`);
          });
        }
      });
    });
  };

  class QuickAddOptions extends HTMLElement {
    constructor() {
      super();
      this.variants = [];
      this.selectedColor = '';
      this.currentVariantId = null;
    }

    connectedCallback() {
      this.sizeIndex = Number(this.dataset.sizeIndex);
      this.colorIndex = Number(this.dataset.colorIndex);
      this.sizeButtons = Array.from(this.querySelectorAll('[data-quick-size]'));
      this.toggleButton = this.querySelector('[data-quick-options-toggle]');
      this.submitButton = this.querySelector('[data-quick-options-submit]');
      this.variantInput = this.querySelector('input[name="id"]');
      this.status = this.querySelector('[data-quick-options-status]');

      this.toggleButton?.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        const isOpen = this.classList.toggle('is-open');
        this.toggleButton.setAttribute('aria-expanded', String(isOpen));
      });

      this.sizeButtons.forEach((button) => {
        button.addEventListener('click', (event) => this.addSizeToCart(event, button));
      });

      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.unsubscribeUpdate = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
          if (String(event?.productVariantId) !== String(this.currentVariantId)) return;
          this.finishRequest(true);
        });
        this.unsubscribeError = subscribe(PUB_SUB_EVENTS.cartError, (event) => {
          if (String(event?.productVariantId) !== String(this.currentVariantId)) return;
          this.finishRequest(false, event?.message || event?.errors);
        });
      }
    }

    disconnectedCallback() {
      this.unsubscribeUpdate?.();
      this.unsubscribeError?.();
      window.clearTimeout(this.requestTimer);
    }

    setProductData(variants, selectedColor) {
      this.variants = variants || [];
      this.selectedColor = selectedColor || '';
      this.updateSizeAvailability();
    }

    setSelectedColor(color) {
      this.selectedColor = color || '';
      this.updateSizeAvailability();
    }

    findVariant(size) {
      return this.variants.find((variant) => {
        if (!variant.available || variant.options?.[this.sizeIndex] !== size) return false;
        if (this.colorIndex >= 0 && this.selectedColor) {
          return variant.options?.[this.colorIndex] === this.selectedColor;
        }
        return true;
      });
    }

    updateSizeAvailability() {
      if (!this.variants.length) return;
      this.sizeButtons.forEach((button) => {
        const variant = this.findVariant(button.dataset.sizeValue);
        const unavailable = !variant;
        button.disabled = unavailable;
        button.setAttribute('aria-disabled', String(unavailable));
        button.classList.toggle('is-unavailable', unavailable);
        if (this.dataset.showSoldOut !== 'true') {
          button.hidden = unavailable;
        }
      });
    }

    addSizeToCart(event, button) {
      event.preventDefault();
      event.stopPropagation();
      if (button.disabled || this.classList.contains('is-loading')) return;

      const variant = this.findVariant(button.dataset.sizeValue);
      if (!variant || !this.variantInput || !this.submitButton) return;

      this.currentVariantId = variant.id;
      this.variantInput.value = variant.id;
      this.classList.add('is-loading');
      this.sizeButtons.forEach((item) => item.classList.toggle('is-loading', item === button));
      if (this.status) this.status.textContent = this.dataset.addingLabel || 'Adding…';
      window.clearTimeout(this.requestTimer);
      this.requestTimer = window.setTimeout(
        () => this.finishRequest(false, this.dataset.errorLabel || 'Unable to add this size'),
        12000
      );
      this.submitButton.click();
    }

    finishRequest(success, message = '') {
      window.clearTimeout(this.requestTimer);
      this.classList.remove('is-loading');
      this.sizeButtons.forEach((item) => item.classList.remove('is-loading'));
      if (this.status) {
        this.status.textContent = success
          ? this.dataset.addedLabel || 'Added to cart'
          : message || this.dataset.errorLabel || 'Unable to add this size';
      }
      if (success) {
        this.classList.remove('is-open');
        this.toggleButton?.setAttribute('aria-expanded', 'false');
      }
      window.setTimeout(() => {
        if (this.status) this.status.textContent = '';
      }, 2600);
    }
  }

  if (!customElements.get('quick-add-options')) {
    customElements.define('quick-add-options', QuickAddOptions);
  }

  const initializeWithin = (root = document) => {
    if (root.matches?.('.product-card')) initializeCard(root);
    root.querySelectorAll?.('.product-card').forEach(initializeCard);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initializeWithin(document), { once: true });
  } else {
    initializeWithin(document);
  }

  document.addEventListener('shopify:section:load', (event) => initializeWithin(event.target));
  document.addEventListener('heading-slider:load', () => initializeWithin(document));

  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (node.nodeType === Node.ELEMENT_NODE) initializeWithin(node);
      });
    });
  });
  observer.observe(document.documentElement, { childList: true, subtree: true });
})();
